import React from "react";
import Tabs from "@bmi-digital/components/tabs";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import LeadBlock from "@bmi-digital/components/lead-block";
import MediaGallery from "@bmi-digital/components/media-gallery";
import Tab, { type TabProps } from "@mui/material/Tab";
import AssetsIframe from "../../../components/AssetsIframe";
import {
  StyledDesktopProductLeadBlock,
  StyledTabPanel
} from "../../../components/styles/ProductLeadBlock.styles";
import { useSiteContext } from "../../../components/Site";
import { transformImages } from "../../../utils/product-details-transforms";
import withGTM from "../../../utils/google-tag-manager";
import ProductDocuments from "./product-documents";
import TechnicalSpecifications from "./technical-specifications";
import AboutTabContent from "./about-tab-content";
import FixingToolIframe from "./fixing-tool-iframe";
import SpecificationIframe from "./specification-iframe";
import type { ProductLeadBlockProps } from "../types";

const GTMTab = withGTM<TabProps>(Tab, {
  label: "label"
});

const TabComponent = (props: Parameters<typeof GTMTab>[0]) => {
  return (
    <GTMTab
      gtm={{ id: "selector-tabs1", action: "Selector – Tabs" }}
      {...props}
    />
  );
};

const DesktopProductLeadBlock = ({
  product,
  sidebarItems,
  pdpFixingToolTitle,
  pdpFixingToolDescription,
  pdpSpecificationTitle,
  pdpSpecificationDescription,
  documentDisplayFormat
}: ProductLeadBlockProps) => {
  const { getMicroCopy } = useSiteContext();

  return (
    <StyledDesktopProductLeadBlock>
      <Tabs initialValue="one" tabComponent={TabComponent}>
        <StyledTabPanel
          heading={getMicroCopy(microCopy.PDP_LEAD_BLOCK_ABOUT)}
          index="one"
          data-testid="aboutTab"
        >
          <AboutTabContent
            productBenefits={product.productBenefits}
            description={product.description}
            guaranteesAndWarrantiesImages={
              product.guaranteesAndWarrantiesImages
            }
            guaranteesAndWarrantiesLinks={product.guaranteesAndWarrantiesLinks}
            awardsAndCertificateDocuments={
              product.awardsAndCertificateDocuments
            }
            awardsAndCertificateImages={product.awardsAndCertificateImages}
            sidebarItems={sidebarItems}
          />
        </StyledTabPanel>
        <StyledTabPanel
          heading={getMicroCopy(
            microCopy.PDP_LEAD_BLOCK_TECHNICAL_SPECIFICATIONS
          )}
          index="two"
          data-testid="technicalSpecificationsTab"
        >
          <TechnicalSpecifications
            product={product}
            sidebarItems={sidebarItems}
          />
        </StyledTabPanel>
        {product.productDocuments.length && (
          <StyledTabPanel
            heading={getMicroCopy(microCopy.PDP_LEAD_BLOCK_DOCUMENTS)}
            index="three"
            data-testid="documentsTab"
          >
            <ProductDocuments
              productDocuments={product.productDocuments}
              documentDisplayFormat={documentDisplayFormat}
            />
          </StyledTabPanel>
        )}
        {product.bimIframeUrl && (
          <StyledTabPanel
            heading={getMicroCopy(microCopy.PDP_LEAD_BLOCK_BIM)}
            index="four"
            data-testid="bimIframeTab"
          >
            <AssetsIframe url={product.bimIframeUrl} title={"BIM iFrame"} />
          </StyledTabPanel>
        )}
        {product.techDrawings.length > 0 && (
          <StyledTabPanel
            heading={getMicroCopy(microCopy.PDP_LEAD_BLOCK_TECHNICAL_DRAWINGS)}
            index="five"
            data-testid="technicalDrawings"
          >
            <LeadBlock justifyContent="center">
              <LeadBlock.Content>
                <LeadBlock.Content.Section>
                  <MediaGallery
                    media={transformImages(product.techDrawings)}
                    videoButtonLabel={getMicroCopy(microCopy.MEDIA_VIDEO)}
                    visualiserButtonLabel={getMicroCopy(microCopy.MEDIA_3D)}
                    visualiserText={getMicroCopy(
                      microCopy.MEDIA_VISUALIZER_TEXT
                    )}
                  />
                </LeadBlock.Content.Section>
              </LeadBlock.Content>
            </LeadBlock>
          </StyledTabPanel>
        )}
        {product.specificationIframeUrl && (
          <StyledTabPanel
            heading={getMicroCopy(microCopy.PDP_LEAD_BLOCK_SPECIFICATION)}
            index="six"
            data-testid="specification"
          >
            <SpecificationIframe
              specificationIframeUrl={product.specificationIframeUrl}
              pdpSpecificationDescription={pdpSpecificationDescription}
              pdpSpecificationTitle={pdpSpecificationTitle}
            />
          </StyledTabPanel>
        )}
        {product.fixingToolIframeUrl && (
          <StyledTabPanel
            heading={getMicroCopy(microCopy.PDP_LEAD_BLOCK_FIXING_TOOL)}
            index="seven"
            data-testid="fixingTool"
          >
            <FixingToolIframe
              fixingToolIframeUrl={product.fixingToolIframeUrl}
              pdpFixingToolTitle={pdpFixingToolTitle}
              pdpFixingToolDescription={pdpFixingToolDescription}
            />
          </StyledTabPanel>
        )}
      </Tabs>
    </StyledDesktopProductLeadBlock>
  );
};

export default DesktopProductLeadBlock;
